/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import FocusLock from 'react-focus-lock';

import { unstable } from '@broadleaf/admin-components';

const { useDebouncedCallback, Portal } = unstable;

export const Popover = ({
  active,
  arrow,
  children,
  className,
  color,
  focusLock,
  offset,
  position,
  parent,
  type
}) => {
  if (!active) {
    return null;
  }
  return (
    <PopoverPortal
      arrow={arrow}
      children={children}
      className={className}
      color={color}
      focusLock={focusLock}
      offset={offset}
      position={position}
      parent={parent}
      type={type}
    />
  );
};

Popover.propTypes = {
  active: PropTypes.bool,
  arrow: PropTypes.bool,
  className: PropTypes.string,
  // purgecss: tw-popover-transparent tw-popover-black tw-popover-white tw-popover-gray-100 tw-popover-gray-200 tw-popover-gray-300 tw-popover-gray-400 tw-popover-gray-500 tw-popover-gray-600 tw-popover-gray-700 tw-popover-gray-800 tw-popover-gray-900 tw-popover-red-100 tw-popover-red-200 tw-popover-red-300 tw-popover-red-400 tw-popover-red-500 tw-popover-red-600 tw-popover-red-700 tw-popover-red-800 tw-popover-red-900 tw-popover-orange-100 tw-popover-orange-200 tw-popover-orange-300 tw-popover-orange-400 tw-popover-orange-500 tw-popover-orange-600 tw-popover-orange-700 tw-popover-orange-800 tw-popover-orange-900 tw-popover-yellow-100 tw-popover-yellow-200 tw-popover-yellow-300 tw-popover-yellow-400 tw-popover-yellow-500 tw-popover-yellow-600 tw-popover-yellow-700 tw-popover-yellow-800 tw-popover-yellow-900 tw-popover-green-100 tw-popover-green-200 tw-popover-green-300 tw-popover-green-400 tw-popover-green-500 tw-popover-green-600 tw-popover-green-700 tw-popover-green-800 tw-popover-green-900 tw-popover-teal-100 tw-popover-teal-200 tw-popover-teal-300 tw-popover-teal-400 tw-popover-teal-500 tw-popover-teal-600 tw-popover-teal-700 tw-popover-teal-800 tw-popover-teal-900 tw-popover-blue-100 tw-popover-blue-200 tw-popover-blue-300 tw-popover-blue-400 tw-popover-blue-500 tw-popover-blue-600 tw-popover-blue-700 tw-popover-blue-800 tw-popover-blue-900 tw-popover-indigo-100 tw-popover-indigo-200 tw-popover-indigo-300 tw-popover-indigo-400 tw-popover-indigo-500 tw-popover-indigo-600 tw-popover-indigo-700 tw-popover-indigo-800 tw-popover-indigo-900 tw-popover-purple-100 tw-popover-purple-200 tw-popover-purple-300 tw-popover-purple-400 tw-popover-purple-500 tw-popover-purple-600 tw-popover-purple-700 tw-popover-purple-800 tw-popover-purple-900 tw-popover-pink-100 tw-popover-pink-200 tw-popover-pink-300 tw-popover-pink-400 tw-popover-pink-500 tw-popover-pink-600 tw-popover-pink-700 tw-popover-pink-800 tw-popover-pink-900 tw-popover-primary-100 tw-popover-primary-200 tw-popover-primary-300 tw-popover-primary-400 tw-popover-primary-500 tw-popover-primary-600 tw-popover-primary-700 tw-popover-primary-800 tw-popover-primary-900 tw-popover-secondary-100 tw-popover-secondary-200 tw-popover-secondary-300 tw-popover-secondary-400 tw-popover-secondary-500 tw-popover-secondary-600 tw-popover-secondary-700 tw-popover-secondary-800 tw-popover-secondary-900 tw-popover-link-100 tw-popover-link-200 tw-popover-link-300 tw-popover-link-400 tw-popover-link-500 tw-popover-link-600 tw-popover-link-700 tw-popover-link-800 tw-popover-link-900
  color: PropTypes.string,
  /**
   * Indicates the popover should trap the user's focus. Useful for modal and
   * dropdowns where you want the user's keyboarding tab focus to stay within
   * the portal until it is closed.
   */
  focusLock: PropTypes.bool,
  offset: PropTypes.number,
  // purgecss: tw-popover-top tw-popover-top-left tw-popover-top-right tw-popover-right tw-popover-right-top tw-popover-right-bottom tw-popover-bottom tw-popover-bottom-left tw-popover-bottom-right tw-popover-left tw-popover-left-top tw-popover-left-bottom
  position: PropTypes.oneOf([
    'top',
    'top-left',
    'top-right',
    'right',
    'right-top',
    'right-bottom',
    'bottom',
    'bottom-left',
    'bottom-right',
    'left',
    'left-top',
    'left-bottom'
  ]),
  parent: PropTypes.object,
  /**
   * Type of popover. This affects the z-index positioning of the popover. Defaults to "popover".
   *
   * Z-index ordering as follows:
   *
   * <ul>
   *   <li>dropdown</li>
   *   <li>fixed</li>
   *   <li>modal</li>
   *   <li>popover (default)</li>
   *   <li>tooltip</li>
   * </ul>
   */
  type: PropTypes.oneOf(['dropdown', 'fixed', 'modal', 'popover', 'tooltip'])
};

Popover.defaultProps = {
  arrow: true,
  color: 'white',
  focusLock: false,
  position: 'top',
  offset: 0,
  type: 'popover'
};

const PopoverPortal = ({
  arrow,
  children,
  className,
  color,
  focusLock,
  offset,
  position,
  parent,
  type
}) => {
  const popoverRef = useRef(null);
  const { currentPosition, isBounded, x, y } = useCoords(
    parent,
    popoverRef,
    position,
    offset
  );
  return (
    <Portal element="div">
      <div
        className={cx(
          'tw-popover',
          `tw-popover-${currentPosition}`,
          `tw-popover-${color}`,
          {
            'tw-invisible': !isBounded,
            'tw-z-10': type === 'dropdown',
            'tw-z-20': type === 'fixed',
            'tw-z-30': type === 'modal',
            'tw-z-40': type === 'popover',
            'tw-z-50': type === 'tooltip'
          }
        )}
        ref={popoverRef}
        style={{
          top: y,
          left: x
        }}
      >
        <FocusLock disabled={!focusLock} returnFocus>
          <div className={cx('tw-popover-content', className)}>
            {arrow && <div className="tw-popover-arrow" />}
            {children}
          </div>
        </FocusLock>
      </div>
    </Portal>
  );
};

function useCoords(parentRef, popoverRef, position, offset) {
  const [isBounded, setIsBounded] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(position);
  const [coords, setCoords] = useState(
    getCoords(parentRef, currentPosition, offset)
  );
  const updateCoords = useCallback(() => {
    const nextPosition = getNextPosition(popoverRef, currentPosition);
    const nextCoords = getCoords(parentRef, nextPosition, offset);
    setCurrentPosition(nextPosition);
    setCoords(nextCoords);
    setIsBounded(true);
  }, [currentPosition, offset, parentRef, popoverRef]);

  useEffect(
    () => {
      updateCoords();
    },
    // eslint-disable-next-line
    []
  );

  const debounceUpdateCoords = useDebouncedCallback(updateCoords, 16);
  useEffect(() => {
    window.addEventListener('scroll', debounceUpdateCoords, true);
    window.addEventListener('resize', debounceUpdateCoords);
    return () => {
      window.removeEventListener('resize', debounceUpdateCoords);
      window.removeEventListener('scroll', debounceUpdateCoords, true);
    };
  }, [debounceUpdateCoords]);
  return { currentPosition, isBounded, ...coords };
}

function getNextPosition(popoverRef, currentPosition) {
  if (popoverRef.current) {
    const popoverRect = popoverRef.current.getBoundingClientRect();
    return clampSecondaryPosition(
      popoverRect,
      clampPrimaryPosition(popoverRect, currentPosition)
    );
  }
  return currentPosition;
}

function clampPrimaryPosition(popoverRect, currentPosition) {
  if (exceedsTopBound(popoverRect) && currentPosition.startsWith('top')) {
    return currentPosition.replace('top', 'bottom');
  } else if (
    exceedsLeftBound(popoverRect) &&
    currentPosition.startsWith('left')
  ) {
    return currentPosition.replace('left', 'right');
  } else if (
    exceedsRightBound(popoverRect) &&
    currentPosition.startsWith('right')
  ) {
    return currentPosition.replace('right', 'left');
  } else if (
    exceedsBottomBound(popoverRect) &&
    currentPosition.startsWith('bottom')
  ) {
    return currentPosition.replace('bottom', 'top');
  }
  return currentPosition;
}

function clampSecondaryPosition(popoverRect, currentPosition) {
  if (exceedsTopBound(popoverRect) && currentPosition.endsWith('-bottom')) {
    return currentPosition.replace('bottom', 'top');
  } else if (
    exceedsLeftBound(popoverRect) &&
    currentPosition.endsWith('-right')
  ) {
    return currentPosition.replace('right', 'left');
  } else if (
    exceedsRightBound(popoverRect) &&
    currentPosition.endsWith('-left')
  ) {
    return currentPosition.replace('left', 'right');
  } else if (
    exceedsBottomBound(popoverRect) &&
    currentPosition.endsWith('-top')
  ) {
    return currentPosition.replace('top', 'bottom');
  }
  return currentPosition;
}

function getCoords(parentRef, position, offset) {
  if (parentRef.current) {
    const parentRect = parentRef.current.getBoundingClientRect();
    return getCoordsForRect(parentRect, position, offset);
  }
  return null;
}

function getCoordsForRect(parentRect, position, offset) {
  const { scrollX, scrollY } = getScrollCoords();
  switch (position) {
    case 'top': {
      return {
        x: scrollX + parentRect.left + parentRect.width / 2,
        y: scrollY + parentRect.top - offset
      };
    }
    case 'top-left': {
      return {
        x: scrollX + parentRect.left,
        y: scrollY + parentRect.top - offset
      };
    }
    case 'top-right': {
      return {
        x: scrollX + parentRect.left + parentRect.width,
        y: scrollY + parentRect.top - offset
      };
    }
    case 'right': {
      return {
        x: scrollX + parentRect.left + parentRect.width + offset,
        y: scrollY + parentRect.top + parentRect.height / 2
      };
    }
    case 'right-top': {
      return {
        x: scrollX + parentRect.left + parentRect.width + offset,
        y: scrollY + parentRect.top
      };
    }
    case 'right-bottom': {
      return {
        x: scrollX + parentRect.left + parentRect.width + offset,
        y: scrollY + parentRect.top + parentRect.height
      };
    }
    case 'left': {
      return {
        x: scrollX + parentRect.left - offset,
        y: scrollY + parentRect.top + parentRect.height / 2
      };
    }
    case 'left-top': {
      return {
        x: scrollX + parentRect.left - offset,
        y: scrollY + parentRect.top
      };
    }
    case 'left-bottom': {
      return {
        x: scrollX + parentRect.left - offset,
        y: scrollY + parentRect.top + parentRect.height
      };
    }
    case 'bottom': {
      return {
        x: scrollX + parentRect.left + parentRect.width / 2,
        y: scrollY + parentRect.top + parentRect.height + offset
      };
    }
    case 'bottom-left': {
      return {
        x: scrollX + parentRect.left,
        y: scrollY + parentRect.top + parentRect.height + offset
      };
    }
    case 'bottom-right': {
      return {
        x: scrollX + parentRect.left + parentRect.width,
        y: scrollY + parentRect.top + parentRect.height + offset
      };
    }
    default:
      throw new Error('Unknown position: ' + position);
  }
}

function getScrollCoords() {
  const scrollY =
    window.scrollY !== undefined ? window.scrollY : window.pageYOffset;
  const scrollX =
    window.scrollX !== undefined ? window.scrollX : window.pageXOffset;
  return {
    scrollX,
    scrollY
  };
}

function exceedsRightBound(rect) {
  const { scrollX } = getScrollCoords();
  return rect.right >= window.innerWidth + scrollX - 10;
}

function exceedsLeftBound(rect) {
  const { scrollX } = getScrollCoords();
  return rect.left <= scrollX - 10;
}

function exceedsTopBound(rect) {
  const { scrollY } = getScrollCoords();
  return rect.top <= scrollY - 10;
}

function exceedsBottomBound(rect) {
  const { scrollY } = getScrollCoords();
  return rect.bottom >= window.innerHeight + scrollY - 10;
}

export default Popover;
